import { useEffect, useRef, useState } from 'react';
import { classNames } from './classNames';
import { HabitaButton } from './HabitaControls';
import { habitaControlBorderClass } from './HabitaSurface';

export type HabitaDatePeriod =
  | 'today'
  | 'yesterday'
  | 'week'
  | '7d'
  | '30d'
  | '90d'
  | 'month'
  | 'previous_month'
  | 'previous_quarter'
  | 'year'
  | 'custom';

export interface HabitaDateRangeFilterValue {
  customEnd: string;
  customStart: string;
  period: HabitaDatePeriod;
}

export type HabitaDateBasis = 'creation' | 'stage' | 'status';

export const habitaDateBasisLabel: Record<HabitaDateBasis, string> = {
  creation: 'Data de criação',
  stage: 'Atualização de etapa',
  status: 'Atualização de status',
};

const habitaDateBasisHint: Record<HabitaDateBasis, string> = {
  creation: 'Quando o lead entrou',
  stage: 'Quando avançou no funil',
  status: 'Quando o status do lead mudou',
};

export interface HabitaResolvedDateRange {
  endDate?: string;
  startDate?: string;
}

interface DateRangePopoverPosition {
  left: number;
  maxHeight: number;
  top: number;
  width: number;
}

const datePresetOptions: Array<{ label: string; value: Exclude<HabitaDatePeriod, 'custom'> }> = [
  { label: 'Hoje', value: 'today' },
  { label: 'Ontem', value: 'yesterday' },
  { label: 'Esta semana', value: 'week' },
  { label: 'Últimos 7 dias', value: '7d' },
  { label: 'Últimos 30 dias', value: '30d' },
  { label: 'Últimos 90 dias', value: '90d' },
  { label: 'Este mês', value: 'month' },
  { label: 'Mês passado', value: 'previous_month' },
  { label: 'Último trimestre', value: 'previous_quarter' },
  { label: 'Este ano', value: 'year' },
];

export function normalizeHabitaDate(date = new Date()): Date {
  const normalized = new Date(date);
  normalized.setHours(0, 0, 0, 0);
  return normalized;
}

export function formatHabitaDateInput(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function isHabitaDateInputValue(value: unknown): value is string {
  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const parsed = new Date(`${value}T00:00:00`);
  return Number.isFinite(parsed.getTime()) && formatHabitaDateInput(parsed) === value;
}

function addDays(date: Date, days: number): Date {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
}

function addMonths(date: Date, months: number): Date {
  return new Date(date.getFullYear(), date.getMonth() + months, 1);
}

function dateFromInput(value: string): Date | null {
  if (!isHabitaDateInputValue(value)) return null;
  const [year, month, day] = value.split('-').map(Number);
  return new Date(year, month - 1, day);
}

function compareDateInput(left: string, right: string): number {
  const leftDate = dateFromInput(left);
  const rightDate = dateFromInput(right);
  if (!leftDate || !rightDate) return 0;
  return leftDate.getTime() - rightDate.getTime();
}

function monthRange(offset: number): HabitaResolvedDateRange {
  const now = normalizeHabitaDate();
  const start = new Date(now.getFullYear(), now.getMonth() + offset, 1);
  const end = new Date(now.getFullYear(), now.getMonth() + offset + 1, 0);

  return {
    endDate: formatHabitaDateInput(end),
    startDate: formatHabitaDateInput(start),
  };
}

function previousQuarterRange(): HabitaResolvedDateRange {
  const today = normalizeHabitaDate();
  const currentQuarterStartMonth = Math.floor(today.getMonth() / 3) * 3;
  const previousQuarterStart = new Date(today.getFullYear(), currentQuarterStartMonth - 3, 1);
  const previousQuarterEnd = new Date(today.getFullYear(), currentQuarterStartMonth, 0);

  return {
    endDate: formatHabitaDateInput(previousQuarterEnd),
    startDate: formatHabitaDateInput(previousQuarterStart),
  };
}

export function resolveHabitaPresetDateRange(period: Exclude<HabitaDatePeriod, 'custom'>): HabitaResolvedDateRange {
  const today = normalizeHabitaDate();

  if (period === 'today') {
    const todayInput = formatHabitaDateInput(today);
    return { endDate: todayInput, startDate: todayInput };
  }

  if (period === 'yesterday') {
    const yesterdayInput = formatHabitaDateInput(addDays(today, -1));
    return { endDate: yesterdayInput, startDate: yesterdayInput };
  }

  if (period === 'week') {
    const daysSinceMonday = (today.getDay() + 6) % 7;
    return {
      endDate: formatHabitaDateInput(today),
      startDate: formatHabitaDateInput(addDays(today, -daysSinceMonday)),
    };
  }

  if (period === 'month') return monthRange(0);
  if (period === 'previous_month') return monthRange(-1);
  if (period === 'previous_quarter') return previousQuarterRange();

  if (period === 'year') {
    return {
      endDate: formatHabitaDateInput(today),
      startDate: formatHabitaDateInput(new Date(today.getFullYear(), 0, 1)),
    };
  }

  const daysByPeriod: Record<'7d' | '30d' | '90d', number> = {
    '7d': 7,
    '30d': 30,
    '90d': 90,
  };
  const days = daysByPeriod[period];

  return {
    endDate: formatHabitaDateInput(today),
    startDate: formatHabitaDateInput(addDays(today, -(days - 1))),
  };
}

export function resolveHabitaDateRange(value: HabitaDateRangeFilterValue): HabitaResolvedDateRange {
  if (value.period === 'custom') {
    return {
      endDate: value.customEnd || undefined,
      startDate: value.customStart || undefined,
    };
  }

  return resolveHabitaPresetDateRange(value.period);
}

export function habitaDatePeriodLabel(period: HabitaDatePeriod): string {
  if (period === 'custom') return 'Período personalizado';
  return datePresetOptions.find((option) => option.value === period)?.label ?? 'Últimos 30 dias';
}

export function formatHabitaDateLabel(value?: string): string {
  if (!value) return '—';
  const date = dateFromInput(value);
  if (!date) return '—';
  return date.toLocaleDateString('pt-BR');
}

export function formatHabitaDateRangeLabel(range: HabitaResolvedDateRange): string {
  if (range.startDate && range.endDate) {
    return `${formatHabitaDateLabel(range.startDate)} até ${formatHabitaDateLabel(range.endDate)}`;
  }
  if (range.startDate) return `A partir de ${formatHabitaDateLabel(range.startDate)}`;
  if (range.endDate) return `Até ${formatHabitaDateLabel(range.endDate)}`;
  return 'Período não selecionado';
}

function monthLabel(date: Date): string {
  return date.toLocaleDateString('pt-BR', {
    month: 'long',
    year: 'numeric',
  });
}

function calendarDays(month: Date): Date[] {
  const firstDay = new Date(month.getFullYear(), month.getMonth(), 1);
  const gridStart = addDays(firstDay, -firstDay.getDay());
  return Array.from({ length: 42 }, (_, index) => addDays(gridStart, index));
}

function isDateInSelectedRange(dateInput: string, startInput: string, endInput: string): boolean {
  if (!startInput || !endInput) return false;
  return compareDateInput(dateInput, startInput) >= 0 && compareDateInput(dateInput, endInput) <= 0;
}

function dateFilterButtonLabel(value: HabitaDateRangeFilterValue): string {
  if (value.period === 'custom' && value.customStart && value.customEnd) {
    return `${formatHabitaDateLabel(value.customStart)} até ${formatHabitaDateLabel(value.customEnd)}`;
  }

  return habitaDatePeriodLabel(value.period);
}

function isAfterMaxDate(dateInput: string, maxDateInput?: string): boolean {
  return Boolean(maxDateInput && compareDateInput(dateInput, maxDateInput) > 0);
}

function CalendarRangeMonth({
  endInput,
  maxDateInput,
  month,
  onNext,
  onPrevious,
  onSelectDate,
  showNext = true,
  showPrevious = true,
  startInput,
}: {
  endInput: string;
  maxDateInput?: string;
  month: Date;
  onNext: () => void;
  onPrevious: () => void;
  onSelectDate: (dateInput: string) => void;
  showNext?: boolean;
  showPrevious?: boolean;
  startInput: string;
}) {
  const todayInput = formatHabitaDateInput(normalizeHabitaDate());
  const days = calendarDays(month);
  const weekdayLabels = ['D', 'S', 'T', 'Q', 'Q', 'S', 'S'];

  return (
    <div className="min-w-[252px] rounded-lg p-3">
      <div className="mb-3 flex items-center justify-between gap-2">
        {showPrevious ? (
          <button
            type="button"
            onClick={onPrevious}
            className="inline-flex h-8 w-8 items-center justify-center rounded-md text-textSecondary transition-colors hover:bg-accent hover:text-textPrimary focus:outline-none focus:ring-2 focus:ring-primary/20"
            aria-label="Mês anterior"
          >
            <i className="ri-arrow-left-s-line text-lg" aria-hidden />
          </button>
        ) : (
          <span className="h-8 w-8" aria-hidden />
        )}
        <p className="font-instrument text-sm font-semibold capitalize text-textPrimary">{monthLabel(month)}</p>
        {showNext ? (
          <button
            type="button"
            onClick={onNext}
            className="inline-flex h-8 w-8 items-center justify-center rounded-md text-textSecondary transition-colors hover:bg-accent hover:text-textPrimary focus:outline-none focus:ring-2 focus:ring-primary/20"
            aria-label="Próximo mês"
          >
            <i className="ri-arrow-right-s-line text-lg" aria-hidden />
          </button>
        ) : (
          <span className="h-8 w-8" aria-hidden />
        )}
      </div>

      <div className="grid grid-cols-7 gap-1">
        {weekdayLabels.map((label, index) => (
          <div key={`${label}-${index}`} className="flex h-7 items-center justify-center font-instrument text-[11px] font-semibold text-textSecondary">
            {label}
          </div>
        ))}
        {days.map((date) => {
          const dateInput = formatHabitaDateInput(date);
          const isCurrentMonth = date.getMonth() === month.getMonth();
          const isDisabled = isAfterMaxDate(dateInput, maxDateInput);
          const isStart = dateInput === startInput;
          const isEnd = dateInput === endInput;
          const isInRange = isDateInSelectedRange(dateInput, startInput, endInput);
          const isToday = dateInput === todayInput;

          return (
            <button
              key={dateInput}
              type="button"
              onClick={() => onSelectDate(dateInput)}
              disabled={isDisabled}
              className={classNames(
                'flex h-8 w-8 items-center justify-center rounded-md font-instrument text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-primary/20 disabled:cursor-not-allowed disabled:opacity-35',
                isCurrentMonth ? 'text-textPrimary' : 'text-textSecondary/45',
                isInRange && !isStart && !isEnd && 'bg-brand-soft text-textPrimary',
                isToday && !isStart && !isEnd && 'ring-1 ring-primary/30',
                (isStart || isEnd) && 'bg-primary font-semibold text-primary-foreground hover:bg-primary',
                !isStart && !isEnd && !isDisabled && 'hover:bg-accent hover:text-textPrimary'
              )}
              aria-pressed={isStart || isEnd}
            >
              {date.getDate()}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function HabitaDateRangeFilter({
  dateBasis = 'creation',
  maxDateInput,
  onApply,
  onClear,
  onDateBasisChange,
  showDateBasis = false,
  value,
}: {
  dateBasis?: HabitaDateBasis;
  maxDateInput?: string;
  onApply: (nextDateFilter: HabitaDateRangeFilterValue) => void;
  onClear: () => void;
  onDateBasisChange?: (nextDateBasis: HabitaDateBasis) => void;
  showDateBasis?: boolean;
  value: HabitaDateRangeFilterValue;
}) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [open, setOpen] = useState(false);
  const [draftPeriod, setDraftPeriod] = useState<HabitaDatePeriod>(value.period);
  const [draftStart, setDraftStart] = useState('');
  const [draftEnd, setDraftEnd] = useState('');
  const [calendarMonth, setCalendarMonth] = useState(() => dateFromInput(resolveHabitaDateRange(value).startDate ?? '') ?? normalizeHabitaDate());
  const [popoverPosition, setPopoverPosition] = useState<DateRangePopoverPosition | null>(null);

  function syncDraftFromValue() {
    const range = resolveHabitaDateRange(value);
    setDraftPeriod(value.period);
    setDraftStart(range.startDate ?? '');
    setDraftEnd(range.endDate ?? '');
    setCalendarMonth(dateFromInput(range.startDate ?? '') ?? normalizeHabitaDate());
  }

  useEffect(() => {
    if (open) syncDraftFromValue();
  }, [open]);

  useEffect(() => {
    if (!open) {
      setPopoverPosition(null);
      return undefined;
    }

    function updatePopoverPosition() {
      const triggerRect = containerRef.current?.getBoundingClientRect();
      if (!triggerRect) return;

      const viewportMargin = 12;
      const popoverGap = 8;
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const width = Math.min(790, Math.max(0, viewportWidth - viewportMargin * 2));
      const left = Math.min(
        Math.max(triggerRect.left, viewportMargin),
        Math.max(viewportMargin, viewportWidth - width - viewportMargin)
      );
      const availableBelow = viewportHeight - triggerRect.bottom - viewportMargin - popoverGap;
      const availableAbove = triggerRect.top - viewportMargin - popoverGap;
      const openAbove = availableBelow < 360 && availableAbove > availableBelow;
      const availableHeight = openAbove ? availableAbove : availableBelow;
      const viewportMaxHeight = Math.max(0, viewportHeight - viewportMargin * 2);
      const maxHeight = Math.min(640, viewportMaxHeight, Math.max(160, Math.max(0, availableHeight)));
      const top = openAbove
        ? Math.max(viewportMargin, triggerRect.top - popoverGap - maxHeight)
        : Math.max(viewportMargin, Math.min(triggerRect.bottom + popoverGap, viewportHeight - viewportMargin - maxHeight));

      setPopoverPosition({ left, maxHeight, top, width });
    }

    updatePopoverPosition();
    window.addEventListener('resize', updatePopoverPosition);
    window.addEventListener('scroll', updatePopoverPosition, true);

    return () => {
      window.removeEventListener('resize', updatePopoverPosition);
      window.removeEventListener('scroll', updatePopoverPosition, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;

    function handleClickOutside(event: MouseEvent) {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false);
    }

    function handleEscape(event: KeyboardEvent) {
      if (event.key === 'Escape') setOpen(false);
    }

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [open]);

  function selectPreset(period: Exclude<HabitaDatePeriod, 'custom'>) {
    const range = resolveHabitaPresetDateRange(period);
    setDraftPeriod(period);
    setDraftStart(range.startDate ?? '');
    setDraftEnd(range.endDate ?? '');
    setCalendarMonth(dateFromInput(range.startDate ?? '') ?? normalizeHabitaDate());
  }

  function selectCalendarDate(dateInput: string) {
    if (isAfterMaxDate(dateInput, maxDateInput)) return;

    setDraftPeriod('custom');
    if (!draftStart || draftEnd || compareDateInput(dateInput, draftStart) < 0) {
      setDraftStart(dateInput);
      setDraftEnd('');
      return;
    }

    setDraftEnd(dateInput);
  }

  function clearDateFilter() {
    const defaultRange = resolveHabitaPresetDateRange('30d');
    setDraftPeriod('30d');
    setDraftStart(defaultRange.startDate ?? '');
    setDraftEnd(defaultRange.endDate ?? '');
    setCalendarMonth(dateFromInput(defaultRange.startDate ?? '') ?? normalizeHabitaDate());
    onClear();
  }

  function applyDateFilter() {
    if (!draftStart || !draftEnd || compareDateInput(draftStart, draftEnd) > 0) return;
    if (draftPeriod === 'custom' && isAfterMaxDate(draftEnd, maxDateInput)) return;

    onApply({
      customEnd: draftPeriod === 'custom' ? draftEnd : '',
      customStart: draftPeriod === 'custom' ? draftStart : '',
      period: draftPeriod,
    });
    setOpen(false);
  }

  const hasInvalidDraft =
    !draftStart ||
    !draftEnd ||
    compareDateInput(draftStart, draftEnd) > 0 ||
    (draftPeriod === 'custom' && isAfterMaxDate(draftEnd, maxDateInput));
  const draftLabel = draftStart && draftEnd ? `${formatHabitaDateLabel(draftStart)} até ${formatHabitaDateLabel(draftEnd)}` : 'Selecione data inicial e final';

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((current) => !current)}
        className={`inline-flex h-9 max-w-full items-center justify-center gap-2 rounded-md border ${habitaControlBorderClass} bg-card px-3 font-instrument text-sm text-textPrimary transition-colors hover:bg-accent focus:outline-none focus:ring-2 focus:ring-primary/20`}
        aria-expanded={open}
        aria-haspopup="dialog"
      >
        <i className="ri-calendar-line text-base text-primary" aria-hidden />
        <span className="max-w-[210px] truncate">{dateFilterButtonLabel(value)}</span>
        {showDateBasis && (
          <span className="max-w-[140px] truncate rounded-sm bg-accent px-1.5 py-0.5 font-instrument text-[10px] font-semibold uppercase tracking-wide text-textSecondary">
            {habitaDateBasisLabel[dateBasis]}
          </span>
        )}
        <i className="ri-arrow-down-s-line text-base text-textSecondary" aria-hidden />
      </button>

      {open && (
        <div
          className={classNames(
            `fixed z-50 overflow-hidden rounded-lg border ${habitaControlBorderClass} bg-card shadow-xl`,
            !popoverPosition && 'invisible'
          )}
          role="dialog"
          aria-label="Selecionar período"
          style={popoverPosition ?? undefined}
        >
          {showDateBasis && (
            <div className="border-b border-border-subtle bg-accent/30 px-3 py-2">
              <p className="px-1 pb-1.5 font-instrument text-[11px] font-semibold uppercase tracking-wide text-textSecondary">
                Filtrar por
              </p>
              <div className="grid grid-cols-3 gap-1.5">
                {(Object.keys(habitaDateBasisLabel) as HabitaDateBasis[]).map((basis) => {
                  const active = dateBasis === basis;

                  return (
                    <button
                      key={basis}
                      type="button"
                      onClick={() => onDateBasisChange?.(basis)}
                      className={classNames(
                        'min-w-0 rounded-md border px-2.5 py-1.5 text-left transition-colors focus:outline-none focus:ring-2 focus:ring-primary/20',
                        active
                          ? 'border-primary bg-card shadow-sm'
                          : 'border-transparent bg-card/60 hover:bg-card'
                      )}
                      aria-pressed={active}
                    >
                      <p className={classNames(
                        'font-instrument text-xs leading-tight text-textPrimary',
                        active ? 'font-semibold' : 'font-medium opacity-80'
                      )}>
                        {habitaDateBasisLabel[basis]}
                      </p>
                      <p className="mt-0.5 font-instrument text-[11px] leading-tight text-textSecondary">
                        {habitaDateBasisHint[basis]}
                      </p>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          <div
            className="flex flex-col overflow-y-auto md:flex-row"
            style={{
              maxHeight: popoverPosition
                ? Math.max(160, popoverPosition.maxHeight - (showDateBasis ? 86 : 0))
                : undefined,
            }}
          >
            <aside className="w-full shrink-0 border-b border-border-subtle p-2 md:w-44 md:border-b-0 md:border-r">
              <p className="px-2 pb-2 pt-1 font-instrument text-[11px] font-semibold uppercase tracking-wide text-textSecondary">Atalhos</p>
              <div className="space-y-0.5">
                {datePresetOptions.map((option) => {
                  const active = draftPeriod === option.value;
                  return (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => selectPreset(option.value)}
                      className={classNames(
                        'flex w-full items-center justify-between rounded-md px-2 py-1.5 text-left font-instrument text-sm transition-colors hover:bg-accent hover:text-textPrimary',
                        active ? 'bg-accent font-medium text-textPrimary' : 'text-textSecondary'
                      )}
                    >
                      <span>{option.label}</span>
                      {active && <span className="h-1.5 w-1.5 rounded-full bg-primary" aria-hidden />}
                    </button>
                  );
                })}
              </div>
            </aside>

            <div className="min-w-0 p-2">
              <div className="overflow-x-auto">
                <div className="grid min-w-[520px] grid-cols-2 gap-2 p-1">
                  <CalendarRangeMonth
                    endInput={draftEnd}
                    maxDateInput={maxDateInput}
                    month={calendarMonth}
                    onNext={() => setCalendarMonth((current) => addMonths(current, 1))}
                    onPrevious={() => setCalendarMonth((current) => addMonths(current, -1))}
                    onSelectDate={selectCalendarDate}
                    showNext={false}
                    startInput={draftStart}
                  />
                  <CalendarRangeMonth
                    endInput={draftEnd}
                    maxDateInput={maxDateInput}
                    month={addMonths(calendarMonth, 1)}
                    onNext={() => setCalendarMonth((current) => addMonths(current, 1))}
                    onPrevious={() => setCalendarMonth((current) => addMonths(current, -1))}
                    onSelectDate={selectCalendarDate}
                    showPrevious={false}
                    startInput={draftStart}
                  />
                </div>
              </div>

              <div className="flex flex-col gap-2 border-t border-border-subtle px-2 py-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex min-w-0 flex-col">
                  <span className="font-instrument text-sm text-textSecondary">{draftLabel}</span>
                  {showDateBasis && (
                    <span className="font-instrument text-[11px] text-textSecondary">
                      Filtrando por: {habitaDateBasisLabel[dateBasis].toLowerCase()}
                    </span>
                  )}
                </div>
                <div className="flex items-center justify-end gap-2">
                  <HabitaButton icon="ri-refresh-line" onClick={clearDateFilter} size="sm" variant="ghost">
                    Limpar
                  </HabitaButton>
                  <HabitaButton icon="ri-check-line" onClick={applyDateFilter} size="sm" disabled={hasInvalidDraft}>
                    Aplicar
                  </HabitaButton>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
